/*
 * The Telegram bridge, ported from telekit's tg.js (MIT) to TypeScript + React hooks.
 *
 * Every call into WebApp is optional: older clients lack methods, and a plain browser
 * has no client at all (telegram-web-app.js still defines WebApp there, with platform
 * "unknown"). This module degrades: haptics go quiet, dialogs are drawn by the app, the
 * main button becomes a bar the app draws itself, and back is a history pop.
 */
import { useEffect, useRef, useSyncExternalStore } from "react";

type Haptic = {
  impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
  notificationOccurred(kind: "error" | "success" | "warning"): void;
  selectionChanged(): void;
};
type BottomButton = {
  setParams(p: { text?: string; is_active?: boolean; is_visible?: boolean; color?: string; text_color?: string }): void;
  onClick(fn: () => void): void;
  offClick(fn: () => void): void;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
};
type WebApp = {
  initData: string;
  initDataUnsafe: { user?: { id: number; first_name: string; last_name?: string; username?: string; photo_url?: string }; auth_date?: number; start_param?: string };
  platform: string;
  version: string;
  colorScheme: "light" | "dark";
  themeParams: Record<string, string>;
  isExpanded: boolean;
  ready(): void;
  expand(): void;
  close(): void;
  disableVerticalSwipes?(): void;
  setHeaderColor?(c: string): void;
  setBackgroundColor?(c: string): void;
  setBottomBarColor?(c: string): void;
  isVersionAtLeast?(v: string): boolean;
  onEvent(e: string, fn: (...a: unknown[]) => void): void;
  offEvent(e: string, fn: (...a: unknown[]) => void): void;
  HapticFeedback?: Haptic;
  MainButton?: BottomButton;
  BackButton?: { show(): void; hide(): void; onClick(fn: () => void): void; offClick(fn: () => void): void };
  showConfirm?(msg: string, cb: (ok: boolean) => void): void;
  showAlert?(msg: string, cb?: () => void): void;
  showPopup?(p: { title?: string; message: string; buttons?: { id?: string; type?: string; text?: string }[] }, cb?: (id: string) => void): void;
  openLink?(url: string, o?: { try_instant_view?: boolean }): void;
  openTelegramLink?(url: string): void;
  enableClosingConfirmation?(): void;
  disableClosingConfirmation?(): void;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp };
  }
}

const tg: WebApp | null = (typeof window !== "undefined" && window.Telegram?.WebApp) || null;

/** True only inside a real Telegram client. */
export const isTelegram = !!(tg && tg.platform && tg.platform !== "unknown");
/** Mini App context = running in Telegram with signed init data. Billing is informational only here (spec 12.3). */
export const isMiniApp = isTelegram && !!tg?.initData;
const client = isTelegram ? tg : null;

export const initData = (tg && tg.initData) || "";
export const tgUser = tg?.initDataUnsafe?.user ?? null;
export const initAuthDate = tg?.initDataUnsafe?.auth_date ?? null;
export const apiVersion = tg?.version ? parseFloat(tg.version) : 0;
export const supports = (v: string) => apiVersion >= parseFloat(v);

function attempt<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

export function initTelegram() {
  if (!client) return false;
  attempt(() => client.ready());
  attempt(() => client.expand());
  if (client.disableVerticalSwipes) attempt(() => client.disableVerticalSwipes!());
  if (client.setHeaderColor) attempt(() => client.setHeaderColor!("secondary_bg_color"));
  if (client.setBackgroundColor) attempt(() => client.setBackgroundColor!("secondary_bg_color"));
  if (client.setBottomBarColor) attempt(() => client.setBottomBarColor!("secondary_bg_color"));
  document.documentElement.classList.add("in-telegram");
  return true;
}

export const haptic = {
  tap(style: "light" | "medium" | "heavy" | "rigid" | "soft" = "light") {
    if (client?.HapticFeedback) attempt(() => client.HapticFeedback!.impactOccurred(style));
  },
  notify(kind: "error" | "success" | "warning" = "success") {
    if (client?.HapticFeedback) attempt(() => client.HapticFeedback!.notificationOccurred(kind));
  },
  select() {
    if (client?.HapticFeedback) attempt(() => client.HapticFeedback!.selectionChanged());
  },
};

/** Open a URL. Inside Telegram it stays in the in-app browser. */
export function openLink(url: string) {
  if (client?.openLink) return client.openLink(url);
  window.open(url, "_blank", "noopener");
}
export function openTelegramLink(url: string) {
  if (client?.openTelegramLink) return client.openTelegramLink(url);
  window.open(url, "_blank", "noopener");
}
export function closeMiniApp() {
  if (client) attempt(() => client.close());
}
export async function copyText(text: string): Promise<boolean> {
  haptic.tap("medium");
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/* ---------------- theme ---------------- */

type Scheme = "light" | "dark";
const themeListeners = new Set<() => void>();
function currentScheme(): Scheme {
  if (client) return client.colorScheme;
  const forced = document.documentElement.dataset.theme;
  if (forced === "light" || forced === "dark") return forced;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
if (typeof window !== "undefined") {
  const fire = () => themeListeners.forEach((l) => l());
  if (client) client.onEvent("themeChanged", fire);
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", fire);
  new MutationObserver(fire).observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
}
/** Re-renders on Telegram themeChanged, OS scheme change, or a manual override. */
export function useColorScheme(): Scheme {
  return useSyncExternalStore(
    (l) => {
      themeListeners.add(l);
      return () => themeListeners.delete(l);
    },
    currentScheme,
    () => "light",
  );
}
export type ThemePref = "system" | "light" | "dark";
export function setThemePref(p: ThemePref) {
  if (p === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = p;
  attempt(() => localStorage.setItem("beacon:theme", p));
}
export function loadThemePref(): ThemePref {
  if (isTelegram) return "system";
  const p = attempt(() => localStorage.getItem("beacon:theme")) as ThemePref | null;
  if (p === "light" || p === "dark") document.documentElement.dataset.theme = p;
  return p ?? "system";
}

/* ---------------- back button (a stack, as in telekit) ---------------- */

const backHandlers: (() => void)[] = [];
if (client?.BackButton) client.BackButton.onClick(() => backHandlers[backHandlers.length - 1]?.());

/** Shows Telegram's BackButton while mounted; the top-most handler wins. */
export function useTelegramBack(handler: (() => void) | null) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!handler) return;
    const fn = () => ref.current?.();
    backHandlers.push(fn);
    if (client?.BackButton) client.BackButton.show();
    return () => {
      const i = backHandlers.lastIndexOf(fn);
      if (i >= 0) backHandlers.splice(i, 1);
      if (client?.BackButton && backHandlers.length === 0) client.BackButton.hide();
    };
  }, [!!handler]); // eslint-disable-line react-hooks/exhaustive-deps
}

/* ---------------- main button ---------------- */

export type MainButtonSpec = { text: string; onClick: () => void; enabled?: boolean; progress?: boolean; destructive?: boolean };
type Claim = { id: number; spec: MainButtonSpec };
const claims: Claim[] = [];
const mbListeners = new Set<() => void>();
let claimSeq = 0;
let nativeHandler: (() => void) | null = null;

function applyNative() {
  if (!client?.MainButton) return;
  const top = claims[claims.length - 1];
  const mb = client.MainButton;
  if (nativeHandler) mb.offClick(nativeHandler);
  if (!top) {
    mb.hide();
    nativeHandler = null;
    return;
  }
  nativeHandler = () => top.spec.onClick();
  mb.onClick(nativeHandler);
  const params: Parameters<BottomButton["setParams"]>[0] = { text: top.spec.text, is_visible: true, is_active: top.spec.enabled !== false };
  if (top.spec.destructive) params.color = getComputedStyle(document.documentElement).getPropertyValue("--tk-destructive").trim() || "#e53935";
  mb.setParams(params);
  if (top.spec.progress) mb.showProgress(false);
  else mb.hideProgress();
}
function notifyMb() {
  applyNative();
  mbListeners.forEach((l) => l());
}

/**
 * Claims the main button while mounted (sheets stack over screens, as in telekit's
 * stash/restore). Inside Telegram this drives WebApp.MainButton; elsewhere the
 * <FallbackMainButton/> renders the top claim as a fixed bar.
 */
export function useMainButton(spec: MainButtonSpec | null) {
  const idRef = useRef<number | null>(null);
  const specRef = useRef(spec);
  specRef.current = spec;
  useEffect(() => {
    if (!spec) return;
    const id = ++claimSeq;
    idRef.current = id;
    claims.push({ id, spec: { ...spec, onClick: () => specRef.current?.onClick() } });
    notifyMb();
    return () => {
      const i = claims.findIndex((c) => c.id === id);
      if (i >= 0) claims.splice(i, 1);
      idRef.current = null;
      notifyMb();
    };
  }, [!!spec]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!spec || idRef.current == null) return;
    const c = claims.find((x) => x.id === idRef.current);
    if (!c) return;
    c.spec = { ...spec, onClick: () => specRef.current?.onClick() };
    notifyMb();
  }, [spec?.text, spec?.enabled, spec?.progress, spec?.destructive]); // eslint-disable-line react-hooks/exhaustive-deps
}
/** The top main-button claim, for the browser fallback bar. Null inside Telegram. */
export function useMainButtonFallback(): MainButtonSpec | null {
  return useSyncExternalStore(
    (l) => {
      mbListeners.add(l);
      return () => mbListeners.delete(l);
    },
    () => (client?.MainButton ? null : claims[claims.length - 1]?.spec ?? null),
    () => null,
  );
}
export const hasNativeMainButton = !!client?.MainButton;

/* ---------------- native dialogs with drawn fallbacks ---------------- */

type DialogButton = { id: string; text: string; type?: "default" | "destructive" | "cancel" };
export type DialogRequest = { title?: string; message: string; buttons: DialogButton[]; resolve: (id: string | null) => void };
const dialogListeners = new Set<(d: DialogRequest | null) => void>();
export function onDialog(fn: (d: DialogRequest | null) => void) {
  dialogListeners.add(fn);
  return () => {
    dialogListeners.delete(fn);
  };
}
function drawDialog(req: Omit<DialogRequest, "resolve">): Promise<string | null> {
  return new Promise((resolve) => {
    const full: DialogRequest = {
      ...req,
      resolve: (id) => {
        dialogListeners.forEach((l) => l(null));
        resolve(id);
      },
    };
    dialogListeners.forEach((l) => l(full));
  });
}
export function confirmDialog(message: string, opts: { title?: string; ok?: string; destructive?: boolean } = {}): Promise<boolean> {
  if (client?.showPopup && supports("6.2")) {
    return new Promise((resolve) =>
      client.showPopup!(
        { title: opts.title, message, buttons: [{ id: "cancel", type: "cancel" }, { id: "ok", type: opts.destructive ? "destructive" : "default", text: opts.ok || "OK" }] },
        (id) => resolve(id === "ok"),
      ),
    );
  }
  return drawDialog({
    title: opts.title,
    message,
    buttons: [
      { id: "cancel", text: "Cancel", type: "cancel" },
      { id: "ok", text: opts.ok || "OK", type: opts.destructive ? "destructive" : "default" },
    ],
  }).then((id) => id === "ok");
}
export function alertDialog(message: string, title?: string): Promise<void> {
  if (client?.showAlert) return new Promise((resolve) => client.showAlert!(message, () => resolve()));
  return drawDialog({ title, message, buttons: [{ id: "ok", text: "OK", type: "default" }] }).then(() => undefined);
}
